import { useQuery } from 'react-query';
import adminApi from '../services/adminApi';

// When seasonId is set, the API uses that season (see server: requestedSeasonId branch first).
// When only teamId is set, the API uses the team's *latest* registration — which can disagree
// with the season selected in the Teams filter, so the UI wrongly shows locked or unlocked.
const checkDivisionLockStatus = (teamId = null, seasonId = null) => {
  const params = new URLSearchParams();
  if (seasonId) {
    params.set('seasonId', seasonId);
  } else if (teamId) {
    params.set('teamId', teamId);
  }
  const q = params.toString();
  return adminApi.get(`/division-lock-system/division-lock-status${q ? `?${q}` : ''}`);
};

// Hook: pass seasonId when the admin has a season filter (authoritative for Teams section).
// Optional teamId: used only when seasonId is absent (fallback: team's latest season).
export const useDivisionLockStatus = (teamId = null, seasonId = null) => {
  const { data: lockStatus, isLoading, error } = useQuery(
    ['division-lock-status', teamId, seasonId],
    () => checkDivisionLockStatus(teamId, seasonId),
    {
      // Lock state does not need sub-minute precision; a shorter interval can overlap
      // slow requests (aborted) or collide with key changes (teamId) + window focus.
      refetchInterval: 90 * 1000,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: true, // Always enabled, even if teamId is null (will check active season)
    }
  );

  // adminApi returns response.data directly, so the structure is different
  const isLocked = lockStatus?.data?.is_locked || lockStatus?.is_locked || false;
  
  return {
    isLocked,
    isLoading,
    error,
    lockStatus: lockStatus?.data || lockStatus,
    seasonId: lockStatus?.seasonId || lockStatus?.data?.seasonId
  };
};

// Hook to check if admin can modify divisions
export const useCanModifyDivisions = () => {
  const { isLocked, isLoading, error } = useDivisionLockStatus();
  
  return {
    canModify: !isLocked,
    isLocked,
    isLoading,
    error
  };
};

// Component to show division lock warning
export const DivisionLockWarning = ({ children, fallback = null }) => {
  const { isLocked, isLoading } = useDivisionLockStatus();

  if (isLoading) {
    return <div>Checking division lock status...</div>;
  }

  if (isLocked) {
    return (
      <div style={{
        padding: '1rem',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        color: '#dc2626',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span>🔒</span>
          <strong>Divisions are currently locked!</strong>
        </div>
        <div style={{ fontSize: '0.875rem' }}>
          You cannot modify team divisions while the division lock is active. 
          Go to the "Division Lock" section to unlock divisions if needed.
        </div>
      </div>
    );
  }

  return children || fallback;
};

// Higher-order component to wrap admin components with division lock protection
export const withDivisionLockProtection = (WrappedComponent, options = {}) => {
  return function DivisionLockProtectedComponent(props) {
    const { isLocked, isLoading } = useDivisionLockStatus();

    if (isLoading) {
      return <div>Loading...</div>;
    }

    if (isLocked && options.preventAction) {
      return (
        <DivisionLockWarning>
          {options.fallback || <div>This action is disabled while divisions are locked.</div>}
        </DivisionLockWarning>
      );
    }

    return <WrappedComponent {...props} isDivisionLocked={isLocked} />;
  };
};

const divisionLockExports = {
  useDivisionLockStatus,
  useCanModifyDivisions,
  DivisionLockWarning,
  withDivisionLockProtection
};

export default divisionLockExports;
